import React, { useEffect, useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Icon, Input } from '@node-space/ui-components'
import { useRoutingNumberValidation } from 'hooks/queries/Beneficiaries/useRoutingNumberValidation'
import { useBicValidation } from 'hooks/queries/useBicValidation'
import { useFilteredCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useAddBeneficiaryContext } from 'hooks/useAddBeneficiaryContext'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import {
  AddIntermediaryBankDetailsPayload,
  AddIntermediaryBankDetailsPayloadKeys,
  BeneficiaryDetailsCurrency,
  ValidateRoutingNumberResponse,
} from 'types/beneficiaries'
import { setIntermediaryBankFieldsFromRoutingNumberValidation } from 'utils/beneficiaries'
import { ModalStep } from '../../AddBeneficiary'
import { addIntermediaryBankDetailsSchema } from '../schemas/addIntermediaryBankDetailsSchema'

const fieldNames: AddIntermediaryBankDetailsPayloadKeys[] = [
  'intermediaryBankName',
  'intermediaryBankAddressLine1',
  'intermediaryBankAddressLine2',
  'intermediaryBankCity',
  'intermediaryBankPostCode',
  'intermediaryBankRegion',
  'intermediaryBankCountry',
]

interface BeneficiaryBankDetailsProps {
  onBack: () => void
  onContinue: (bankDetails: AddIntermediaryBankDetailsPayload, skipDetails: boolean) => void
  isSubmitting: boolean
  skipStep: (nextStep: ModalStep) => void
}

export const IntermediaryBankDetails = ({
  onContinue,
  isSubmitting,
  skipStep,
}: BeneficiaryBankDetailsProps) => {
  const { t } = useTranslation()
  const { data: countries, isPending: countriesLoading } = useFilteredCountriesQuery(true)
  const {
    beneficiaryFormState,
    beneficiaryFormErrors,
    setBeneficiaryFormErrors,
    setBeneficiaryIbanValidationState,
    beneficiaryBicValidationState,
    setBeneficiaryBicValidationState,
  } = useAddBeneficiaryContext()
  const formSchema = addIntermediaryBankDetailsSchema(t)
  const { setRequestError } = useMappedErrorMessage()
  const { register, formState, setValue, control, trigger, clearErrors, watch, setError } =
    useForm<AddIntermediaryBankDetailsPayload>({
      resolver: yupResolver(formSchema),
    })

  const [
    intermediaryBankName,
    intermediaryBic,
    intermediaryBankRoutingNumber,
    intermediaryBankAddressLine1,
    intermediaryBankAddressLine2,
    intermediaryBankCity,
    intermediaryBankPostCode,
    intermediaryBankRegion,
    intermediaryBankCountry,
  ] = useWatch({
    control,
    name: [
      'intermediaryBankName',
      'intermediaryBic',
      'intermediaryBankRoutingNumber',
      'intermediaryBankAddressLine1',
      'intermediaryBankAddressLine2',
      'intermediaryBankCity',
      'intermediaryBankPostCode',
      'intermediaryBankRegion',
      'intermediaryBankCountry',
    ],
    defaultValue: {
      intermediaryBankName: '',
      intermediaryBic: '',
      intermediaryBankRoutingNumber: '',
      intermediaryBankAddressLine1: '',
      intermediaryBankAddressLine2: '',
      intermediaryBankCity: '',
      intermediaryBankPostCode: '',
      intermediaryBankRegion: '',
      intermediaryBankCountry: '',
    },
  })

  const formValues = watch()

  const { isFetching: isValidatingRoutingNumber, refetch: validateRoutingNumber } =
    useRoutingNumberValidation(intermediaryBankRoutingNumber, setRequestError, false)

  const {
    data: bicValidation,
    refetch: validateBic,
    isFetching: isValidatingBic,
  } = useBicValidation(intermediaryBic, setRequestError, false)

  const isUSD = beneficiaryFormState?.currencyCode === BeneficiaryDetailsCurrency.USD
  const isUSDAndUS = isUSD && formValues?.intermediaryBankCountry === 'US'
  const isIntermediaryBicValid = bicValidation === 'Valid'

  useEffect(() => {
    setCachedValues()
  }, [])

  useEffect(() => {
    if (beneficiaryFormErrors) {
      Object.keys(beneficiaryFormErrors).forEach((field: AddIntermediaryBankDetailsPayloadKeys) => {
        setError(field, beneficiaryFormErrors[field])
      })
    } else {
      clearErrors()
    }
  }, [setError, clearErrors, beneficiaryFormErrors])

  const setCachedValues = () => {
    Object.keys(beneficiaryFormState).forEach((field: keyof AddIntermediaryBankDetailsPayload) => {
      if (beneficiaryFormState[field]) {
        setValue(field, beneficiaryFormState[field])
      }
    })
  }

  const countryOptions = useMemo(() => {
    if (countries?.length) {
      return countries?.map(country => ({
        leftText: country?.name,
        value: country?.code,
      }))
    }

    return []
  }, [countries])

  const handleIntermediaryCountrySelect = (countryCode: string) => {
    const countryName = countryOptions.find(country => country?.value === countryCode)?.leftText
    setValue('intermediaryBankCountryName', countryName)
    setValue('intermediaryBankCountry', countryCode)
  }

  const handleChange = (field: keyof AddIntermediaryBankDetailsPayload, value: string) => {
    setValue(field, value)
  }

  const setBankDetailsFromRoutingNumberApi = (
    bankDetailsResponse: ValidateRoutingNumberResponse
  ) => {
    const details = setIntermediaryBankFieldsFromRoutingNumberValidation(bankDetailsResponse)

    setBeneficiaryIbanValidationState(details)
    if (Object.keys(details || {})?.length) {
      Object.keys(details)?.forEach(detail => {
        if (detail === 'bankCountry') {
          handleIntermediaryCountrySelect(details[detail])
        } else {
          handleChange(detail as AddIntermediaryBankDetailsPayloadKeys, details[detail])
        }
      })
    }
  }

  const handleOnRoutingNumberBlur = async () => {
    if (intermediaryBankRoutingNumber) {
      clearErrors()
      const isFormFieldValid = await trigger('intermediaryBankRoutingNumber')
      const response = isFormFieldValid && (await validateRoutingNumber())
      const bankDetails = response?.data
      const isValidRoutingNumber =
        !!bankDetails?.bankDetails?.bankName && !bankDetails?.validationFailed?.length

      if (response?.isError) {
        setBeneficiaryFormErrors({
          ...beneficiaryFormErrors,
          intermediaryBankRoutingNumber: { message: t('invalidRoutingNumberErrorMessage') },
        })

        return
      }

      if (isValidRoutingNumber) {
        const { bankAddress, bankName } = bankDetails?.bankDetails || {}
        const { addressLine1, addressLine2, city, region, postCode, countryCode } =
          bankAddress || {}
        handleChange('intermediaryBankName', bankName)
        handleChange('intermediaryBankAddressLine1', addressLine1)
        handleChange('intermediaryBankAddressLine2', addressLine2)
        handleChange('intermediaryBankCity', city)
        handleChange('intermediaryBankRegion', region)
        handleChange('intermediaryBankPostCode', postCode)
        handleChange('intermediaryBankCountry', countryCode)
        setBankDetailsFromRoutingNumberApi(bankDetails)
      } else if (bankDetails?.validationFailed?.length) {
        setBeneficiaryFormErrors({
          ...beneficiaryFormErrors,
          intermediaryBankRoutingNumber: { message: t('invalidRoutingNumberErrorMessage') },
        })
      }
    }
  }

  const handleIntermediaryBicOnBlur = async () => {
    if (beneficiaryBicValidationState) {
      setBeneficiaryBicValidationState(null)
    }
    clearErrors()
    if (intermediaryBic) {
      const bicValidationResponse = await validateBic()
      if (bicValidationResponse && bicValidationResponse?.data !== 'Valid') {
        setBeneficiaryFormErrors({
          ...beneficiaryFormErrors,
          intermediaryBic: { message: t('invalidBicCodeErrorMessage') },
        })
      } else {
        setBeneficiaryBicValidationState(bicValidationResponse?.data)
      }
    }
  }

  const submitBankAddressAccountDetails = async () => {
    const validateFields: AddIntermediaryBankDetailsPayloadKeys[] = [
      'intermediaryBankName',
      'intermediaryBic',
      'intermediaryBankAddressLine1',
      'intermediaryBankAddressLine2',
      'intermediaryBankCity',
      'intermediaryBankPostCode',
      'intermediaryBankRegion',
      'intermediaryBankCountry',
    ]

    const isRoutingNumberInvalid =
      isUSD &&
      formState?.errors?.intermediaryBankRoutingNumber?.message ===
        t('invalidRoutingNumberErrorMessage')

    if (isRoutingNumberInvalid) {
      clearErrors(fieldNames)

      await trigger(validateFields)
      return
    }

    if (isUSD) {
      validateFields.push('intermediaryBankRoutingNumber')
    }

    clearErrors()

    const verifyFields = await trigger(validateFields)

    if (verifyFields) {
      return onContinue(formValues, false)
    }
  }

  return (
    <Box padding={24} flex flexCol width="full">
      <Box flexCol width="full" paddingB={16}>
        {isUSD && (
          <Box flexSize="fill" paddingB={16}>
            <Combobox
              {...register('intermediaryBankCountry')}
              label={t('country')}
              id="bank-country"
              selectPlaceholder={t('searchOrSelectACountry')}
              items={countryOptions}
              onChange={handleIntermediaryCountrySelect}
              loading={countriesLoading || isValidatingRoutingNumber}
              defaultValue={intermediaryBankCountry}
            />
          </Box>
        )}

        <Input
          {...register('intermediaryBankName')}
          label={t('manageBeneficiaries.intermediarysBankName')}
          name="intermediaryBankName"
          test-id="input-intermediary-bank-name"
          errorText={formState?.errors?.intermediaryBankName?.message}
          error={!!formState?.errors?.intermediaryBankName}
          value={intermediaryBankName}
          onChange={event => handleChange('intermediaryBankName', event?.target?.value)}
          placeholder={t('manageBeneficiaries.enterIntermediaryBankName')}
        />
      </Box>
      <Box flexCol width="full" paddingB={16}>
        <Input
          {...register('intermediaryBic')}
          label={t('bic')}
          name="intermediaryBic"
          test-id="input-intermediary-bic"
          errorText={formState?.errors?.intermediaryBic?.message}
          error={!!formState?.errors?.intermediaryBic}
          value={intermediaryBic}
          onChange={event => handleChange('intermediaryBic', event?.target?.value)}
          placeholder={t('inputPlaceholders.enterBicPlaceholder')}
          IconButton={
            isIntermediaryBicValid ? (
              <Button variant="ghost" iconElement={<Icon name="Check" />} />
            ) : null
          }
          iconPosition="right"
          onBlur={handleIntermediaryBicOnBlur}
          loading={isValidatingBic}
        />
      </Box>

      {isUSDAndUS && (
        <Box flexCol width="full" paddingB={16}>
          <Input
            {...register('intermediaryBankRoutingNumber')}
            label={t('abaRoutingNumber')}
            inputMode="text"
            name="intermediaryBankRoutingNumber"
            test-id="input-intermediary-bank-routing-number"
            errorText={formState?.errors?.intermediaryBankRoutingNumber?.message}
            error={!!formState?.errors?.intermediaryBankRoutingNumber}
            value={intermediaryBankRoutingNumber}
            onChange={event => handleChange('intermediaryBankRoutingNumber', event?.target?.value)}
            optionalText={t('optional')}
            onBlur={handleOnRoutingNumberBlur}
            placeholder={t('abaRoutingNumberPlaceholder')}
            loading={isValidatingRoutingNumber}
          />
        </Box>
      )}

      <Box flexCol width="full" paddingB={16}>
        <Input
          {...register('intermediaryBankAddressLine1')}
          label={t('bankAddressLine1')}
          inputMode="text"
          name="intermediaryBankAddressLine1"
          test-id="input-intermediary-bank-address-line-1"
          errorText={formState?.errors?.intermediaryBankAddressLine1?.message}
          error={!!formState?.errors?.intermediaryBankAddressLine1}
          value={intermediaryBankAddressLine1}
          onChange={event => handleChange('intermediaryBankAddressLine1', event?.target?.value)}
          placeholder={t('inputPlaceholders.enterAddressLine1Placeholder')}
        />
      </Box>
      <Box flexCol width="full" paddingB={16}>
        <Input
          {...register('intermediaryBankAddressLine2')}
          label={t('bankAddressLine2')}
          inputMode="text"
          name="intermediaryBankAddressLine2"
          test-id="input-intermediary-bank-address-line-2"
          errorText={formState?.errors?.intermediaryBankAddressLine2?.message}
          error={!!formState?.errors?.intermediaryBankAddressLine2}
          value={intermediaryBankAddressLine2}
          onChange={event => handleChange('intermediaryBankAddressLine2', event?.target?.value)}
          placeholder={t('inputPlaceholders.enterAddressLine2Placeholder')}
          loading={isValidatingRoutingNumber}
        />
      </Box>
      <Box flex gapX={12} paddingB={16}>
        <Box flexSize="auto" width="full">
          <Input
            {...register('intermediaryBankCity')}
            label={t('city')}
            inputMode="text"
            name="city"
            test-id="input-bank-city"
            errorText={formState?.errors?.intermediaryBankCity?.message}
            error={!!formState?.errors?.intermediaryBankCity}
            value={intermediaryBankCity}
            onChange={event => handleChange('intermediaryBankCity', event?.target?.value)}
            placeholder={t('inputPlaceholders.enterCityPlaceholder')}
            loading={isValidatingRoutingNumber}
          />
        </Box>
        <Box flexSize="auto" width="full">
          <Input
            {...register('intermediaryBankPostCode')}
            label={t('postCode')}
            inputMode="text"
            name="bic"
            test-id="input-bank-post-code"
            errorText={formState?.errors?.intermediaryBankPostCode?.message}
            error={!!formState?.errors?.intermediaryBankPostCode}
            value={intermediaryBankPostCode}
            onChange={event => handleChange('intermediaryBankPostCode', event?.target?.value)}
            placeholder={t('inputPlaceholders.enterPostalCodePlaceholder')}
            loading={isValidatingRoutingNumber}
          />
        </Box>
      </Box>
      <Box flexCol width="full" gapX={12} paddingB={16}>
        <Input
          {...register('intermediaryBankRegion')}
          label={t('region')}
          inputMode="text"
          name="intermediaryBankRegion"
          test-id="input-bank-region"
          errorText={formState?.errors?.intermediaryBankRegion?.message}
          error={!!formState?.errors?.intermediaryBankRegion}
          value={intermediaryBankRegion}
          onChange={event => handleChange('intermediaryBankRegion', event?.target?.value)}
          placeholder={t('inputPlaceholders.enterRegionPlaceholder')}
          loading={isValidatingRoutingNumber}
        />
      </Box>
      {!isUSD && (
        <Box flexSize="fill">
          <Combobox
            {...register('intermediaryBankCountry')}
            label={t('country')}
            id="bank-country"
            selectPlaceholder={t('searchOrSelectACountry')}
            items={countryOptions}
            onChange={handleIntermediaryCountrySelect}
            loading={countriesLoading || isValidatingRoutingNumber}
            defaultValue={intermediaryBankCountry}
            errorText={formState?.errors?.intermediaryBankCountry?.message}
          />
        </Box>
      )}

      <Box flex justify="between" paddingT={24}>
        <Button
          onClick={() => {
            clearErrors()
            skipStep(ModalStep.CONFIRMATION)
          }}
          variant="secondary"
          testId="skip"
        >
          {t('skipThisStep')}
        </Button>
        <Button
          testId="add-benefiary"
          onClick={submitBankAddressAccountDetails}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {t('manageBeneficiaries.addBeneficiaryCta')}
        </Button>
      </Box>
    </Box>
  )
}
